import { Component, Inject, OnInit, PLATFORM_ID, ViewChild } from '@angular/core';
import { ErrorStrings, ValidationPatterns } from '../../constants/form.constants';
import { Pet, PetGender, PetRehomingRequest } from '../../interfaces/pet';
import { Alert } from '../../interfaces/alert';
import {
  TokRequest,
  User,
  UserAddress,
  UserAddressType,
  UserTelephone,
  UserTelephoneType,
} from '../../interfaces/user';
import { Species } from '../../interfaces/species';
import { NgbTypeahead } from '@ng-bootstrap/ng-bootstrap';
import { ActivatedRoute, Router } from '@angular/router';
import { PetService } from '../../services/pet/pet.service';
import { UserService } from '../../services/user/user.service';
import { HttpErrorResponse } from '@angular/common/http';
import { merge, Observable, Subject, Subscription } from 'rxjs';
import { debounceTime, distinctUntilChanged, filter, map } from 'rxjs/operators';
import { TokOwnerDetail } from '../../interfaces/Account/ownerDetail';
import { OwnerAddress } from '../../interfaces/Account/ownerAddress';
import { AddressPAF } from '../../services/address-finder/address-finder.service';
import { UpdateOwnerDetailDTO } from '../../interfaces/Account/updateOwnerDetailDTO';
import { FormBuilder, FormControl, FormGroup } from '@angular/forms';
import { atLeastOneCheckboxToBeCheckedValidator } from '../../shared/utils/utils';
import { TOK_PAYMENT } from 'src/app/constants/route.constants';
import { AddressService } from 'src/app/services/address/address.service';
import { isPlatformBrowser } from '@angular/common';

@Component({
  selector: 'app-tok-details',
  templateUrl: './tok-details.component.html',
  styleUrls: ['./tok-details.component.scss'],
})
export class TokDetailsComponent implements OnInit {
  user: User;

  @ViewChild('petBreedTypeahead') petBreedTypeahead: NgbTypeahead;
  focus$ = new Subject<string>();
  click$ = new Subject<string>();

  alert: Alert = null;
  errorStrings = ErrorStrings;
  loadingSpecies = false;
  loadingBreeds = false;
  speciesList: Species[] = [];
  selectedSpecies: Species = null;
  breedNamesList: string[] = [];

  pet: PetRehomingRequest = {
    name: null,
    species: null,
    breed: null,
    gender: null,
    chipNumber: null,
    extraInfo: null,
    vetCode: null,
    existingKeeper: {
      name: null,
      phoneNumber: null,
    },
  };
  petForRehome: Pet = null;
  petGender = PetGender;
  validationPatterns = ValidationPatterns;
  tokRequests: TokRequest[];
  loading = false;
  isBrowser: boolean;

  validationErrorMessage: string;

  public communicationForm: FormGroup;
  public thirdPartyCheckboxStateChanged = false;
  public keeperDetailModel: TokOwnerDetail = {
    firstName: '',
    lastName: '',
    mainPhoneNumber: '',
    secondaryPhoneNumber: '',
  };

  public addressModel: OwnerAddress = {
    addressLine1: '',
    addressLine2: '',
    townPerCity: '',
    postcode: '',
    phoneNumber: '',
    contactName: '',
  };

  public postcodeCheck = null;
  public selectedAddress = null;
  public addressesSelectList = [];
  public isPostcodeInvalid = false;
  private _postcodeCheckChanged: Subject<string> = new Subject<string>();

  private _existingTelephones: { mainPhoneNumber: string, secondaryPhoneNumber: string };
  private _mainAddress: UserAddress = null;
  private _subs: Subscription[] = [];

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private petService: PetService,
    private userService: UserService,
    private _addressService: AddressService,
    private _fb: FormBuilder,
    @Inject(PLATFORM_ID) private platformId: Object,
  ) {
    this.user = this.route.snapshot.data.user as User;
    this.tokRequests = this.user.tokRequests as TokRequest[];

    if (this.tokRequests.length) {
      this.petForRehome = this.petService.getTokRequestPet(this.tokRequests, this.user.pets)
      Object.keys(this.pet).forEach(key => {
        if (this.petForRehome[key] !== undefined) {
          this.pet[key] = this.petForRehome[key];
        }
      });
    }
    this.isBrowser = isPlatformBrowser(this.platformId);
  }

  ngOnInit(): void {
    this.initSubs();
    this.initForm();
    this.loadSpecies();
    this.initCommunicationForm();
  }

  public updateOwnerDetailsAndAddresses() {
    this.loading = true;

    const ownerData: UpdateOwnerDetailDTO = {
      title: '',
      dateOfBirth: new Date(),
      gender: '',
      firstName: this.keeperDetailModel.firstName,
      lastName: this.keeperDetailModel.lastName,
      mainPhoneNumber: this.keeperDetailModel.mainPhoneNumber || '',
      secondaryPhoneNumber: this.keeperDetailModel.secondaryPhoneNumber || '',
    };

    const ownerAddressDTO = [
      {
        line1: this.addressModel.addressLine1,
        line2: this.addressModel.addressLine2,
        town: this.addressModel.townPerCity,
        county: this._mainAddress?.county ? this._mainAddress.county : null,
        postcode: this.addressModel.postcode,
        addressType: UserAddressType.Main,
        phoneNumber: this.addressModel.phoneNumber,
        contactName: this.addressModel.contactName,
        temporary: this._mainAddress?.temporary ? this._mainAddress.temporary : null,
      },
    ];

    this.userService
      .updateOwnerDetailsAndAddresses(ownerData, ownerAddressDTO)
      .subscribe(
        (response) => {
          this.getExistingTelephones();

          this.alert = {
            type: 'success',
            message: "Your detail information was updated. You'll get redirected in a few seconds!",
          };
        },
        (err) => {
          if (err instanceof HttpErrorResponse && err.status === 400) {
            this.alert = {
              type: 'danger',
              message: 'Please enter valid phone numbers.',
            };
          } else {
            this.alert = {
              type: 'danger',
              message: 'An error has occurred. Please try again later.',
            };
          }
        }
      )
      .add(() => {
        this.loading = false;
      });
  }

  private getExistingTelephones(): { mainPhoneNumber: string, secondaryPhoneNumber: string } {
    return {
      mainPhoneNumber: this.user.contact.mainPhoneNumber || '',
      secondaryPhoneNumber: this.user.contact.secondaryPhoneNumber || ''
    }
  }

  private initForm() {
    this._existingTelephones = this.getExistingTelephones();
    this.keeperDetailModel = {
      firstName: this.user.name.firstName,
      lastName: this.user.name.lastName,
      mainPhoneNumber: this._existingTelephones.mainPhoneNumber,
      secondaryPhoneNumber: this._existingTelephones.secondaryPhoneNumber
    };

    if (this._mainAddress) {
      this.addressModel = {
        addressLine1: this._mainAddress.line1,
        addressLine2: this._mainAddress.line2,
        townPerCity: this._mainAddress.town,
        postcode: this._mainAddress.postcode,
      };
    }
  }

  save(): void {
    this.closeAlert();
    this.loading = true;

    this.updateOwnerDetailsAndAddresses();

    this.petService.sendPetTokRequest(this.pet).subscribe(
      () => {
        this.trackEvent('petrehomeresupportemailquest::submitted');
      },
      (err) => {
        this.alert = {
          type: 'danger',
          message: 'An error has occurred. Please try again later.',
        };
      }
    );
    this.router.navigate([`/${TOK_PAYMENT}`], { queryParams: { petName: this.pet.name } });
    return;
  }

  closeAlert() {
    this.alert = null;
  }

  updateBreedsList(): void {
    // Loading breeds list
    this.breedNamesList = [];
    this.loadingBreeds = true;
    this.pet.species = this.selectedSpecies.description;
    this.petService
      .getBreedsFor(this.selectedSpecies.venomCode)
      .subscribe(
        (breedsList) => {
          this.breedNamesList = Object.keys(breedsList);

          if (this.breedNamesList.length === 1) {
            this.pet.breed = this.breedNamesList[0];
          } else {
            this.pet.breed = null;
          }
        },
        (err) => {
          this.alert = {
            type: 'danger',
            message:
              'An error has occurred while retrieving some information. Please try again later.',
          };
        }
      )
      .add(() => {
        this.loadingBreeds = false;
      });
  }

  searchBreed = (text$: Observable<string>) => {
    const debouncedText$ = text$.pipe(debounceTime(200), distinctUntilChanged());
    const clicksWithClosedPopup$ = this.click$.pipe(
      filter(() => !this.petBreedTypeahead.isPopupOpen())
    );
    const inputFocus$ = this.focus$;
    return merge(debouncedText$, inputFocus$, clicksWithClosedPopup$).pipe(
      map((term) => {
        if (term === '') {
          return this.breedNamesList.length <= 10 ? this.breedNamesList : [];
        } else {
          return this.breedNamesList
            .filter((v) => v.toLowerCase().indexOf(term.toLowerCase()) > -1)
            .slice(0, 10);
        }
      })
    );
  };

  private loadSpecies(): void {
    // Loading species list
    this.loadingSpecies = true;
    this.petService
      .getAllSpecies()
      .subscribe(
        (speciesList) => {
          this.speciesList = speciesList;
        },
        (err) => {
          this.alert = {
            type: 'danger',
            message:
              'An error has occurred while retrieving some information. Please try again later.',
          };
        }
      )
      .add(() => {
        this.loadingSpecies = false;
      });
  }

  trackEvent(event: string) {
    if (!this.isBrowser) return;
    window.analytics.track(event);
  }

  public initCommunicationForm() {
    this.communicationForm = this._fb.group({
      subscribeToAllInternalCommunication: [false],
      thirdPartySubscribeOptionSelectGroup: new FormGroup(
        {
          subscribeToAllThirdParty: new FormControl(false),
          unsubscribeFromAllThirdParty: new FormControl(false),
        },
        atLeastOneCheckboxToBeCheckedValidator()
      ),
    });
  }

  public onSubscribeOptionChanged(event) {
    this.thirdPartyCheckboxStateChanged = true;
    if (event.target.checked) {
      if (event.target.id == 'subscribeToAllThirdParty') {
        this.communicationForm.controls['thirdPartySubscribeOptionSelectGroup']
          .get('unsubscribeFromAllThirdParty')
          .setValue(false);
      } else {
        this.communicationForm.controls['thirdPartySubscribeOptionSelectGroup']
          .get('subscribeToAllThirdParty')
          .setValue(false);
      }
    }

    if (!this.communicationForm.controls['thirdPartySubscribeOptionSelectGroup'].errors) {
      this.validationErrorMessage = null;
    } else {
      this.validationErrorMessage = 'Choose your marketing preferences to register!';
    }
  }

  public onPostCodeCheckChanges(inputEvent) {
    this._postcodeCheckChanged.next(inputEvent.target.value);
  }

  public onSelectAddressChanged(selectedAddress: AddressPAF) {
    this.addressModel = {
      addressLine1: this.createAddressLine1(selectedAddress),
      addressLine2: this.addressModel.addressLine2,
      townPerCity: selectedAddress.town,
      postcode: selectedAddress.postcode,
    };
  }

  public getAddressLabel(addressOption: AddressPAF) {
    return this.createAddressLine1(addressOption);
  }

  private initSubs() {
    this._subs.push(
      this._postcodeCheckChanged
        .pipe(debounceTime(1000))
        .subscribe((postcode: string) => {
          this.isPostcodeInvalid = false;
          this.verifyPostcodeAndGetAddressList(postcode);
        })
    );
  }

  private verifyPostcodeAndGetAddressList(postCode: string) {
    this._addressService
      .checkPostcodeValidity(postCode)
      .subscribe((isValidPostcode: boolean) => {
        this.addressesSelectList = [];
        if (isValidPostcode) {
          this._addressService
            .getAddressMatches(postCode)
            .subscribe((addresses: AddressPAF[]) => {
              this.addressesSelectList = addresses;
            });
        } else {
          this.isPostcodeInvalid = true;
        }
      });
  }

  private createAddressLine1(selectedAddress: AddressPAF): string {
    let result = `${selectedAddress.thoroughfare}`;
    if (selectedAddress.buildingNumber && selectedAddress.buildingNumber.length > 0) {
      result = `${selectedAddress.buildingNumber}, ` + result;
    }

    if (selectedAddress.buildingName && selectedAddress.buildingName.length > 0) {
      result = `${selectedAddress.buildingName}, ` + result;
    }

    if (selectedAddress.subBuildingName && selectedAddress.subBuildingName.length > 0) {
      result = `${selectedAddress.subBuildingName}, ` + result;
    }

    if (selectedAddress.organisationName && selectedAddress.organisationName.length > 0) {
      result = `${selectedAddress.organisationName}, ` + result;
    }

    return result;
  }
}
